import {
  CreateParams,
  CreateResult,
  GetListParams,
  GetListResult,
  GetOneParams,
  GetOneResult,
  UpdateParams,
  UpdateResult,
} from "react-admin";
import { ExamAuthorityFeeDto, getAuthenticatedServerClient, schemas } from "../api/server.api";
import { AbstractDataProvider } from "./AbstractDataProvider";
import { DateTime } from "luxon";
import { v4 as randomUuid } from "uuid";
import { applyFilter, applyPagination, applySort } from "../backoffice.utils";
import _isEqual from "lodash/isEqual";
import { DrivingLicenseClass } from "../model/DrivingLicenseClass";
import { Money, MoneySchema } from "../model/Money";

// If there is Aufstieg, the label is the driving license class + "Aufstieg", otherwise it is the driving license class
export type DrivingLicenseClassLabel = DrivingLicenseClass | "A Aufstieg" | "A2 Aufstieg";

export type ExamAuthorityFee = Omit<
  ExamAuthorityFeeDto,
  "validFrom" | "validUntil" | "theoryExamFee" | "practicalExamFee"
> & {
  id: string;
  drivingLicenseClassLabel: DrivingLicenseClassLabel;
  validFrom: DateTime;
  validUntil: DateTime | null;
  theoryExamFee: Money | null;
  practicalExamFee: Money | null;
};

class ExamAuthorityFeesProvider extends AbstractDataProvider<ExamAuthorityFee> {
  async getOne(_resource: string, { id }: GetOneParams<ExamAuthorityFee>): Promise<GetOneResult<ExamAuthorityFee>> {
    const serverClient = await getAuthenticatedServerClient();
    const dto = await serverClient.getExamAuthorityFee({ params: { id } });
    return { data: _dtoToRecord(dto) };
  }

  async getList(
    _resource: string,
    { filter, sort, pagination }: GetListParams,
  ): Promise<GetListResult<ExamAuthorityFee>> {
    const serverClient = await getAuthenticatedServerClient();
    const dto = await serverClient.getAllExamAuthorityFees();
    const records = dto.map(_dtoToRecord);
    return applyPagination(applySort(applyFilter(records, filter), sort), pagination);
  }

  async create(_resource: string, { data }: CreateParams<ExamAuthorityFee>): Promise<CreateResult<ExamAuthorityFee>> {
    const idempotencyKey = randomUuid();
    const { validFrom, validUntil, drivingLicenseClassLabel, ...rest } = data;
    if (!drivingLicenseClassLabel) throw new Error("Property drivingLicenseClassLabel must be provided.");
    const [drivingLicenseClass, isDrivingLicenseClassAdvancement] =
      _splitDrivingLicenseClassLabel(drivingLicenseClassLabel);

    const createDto = schemas.CreateExamAuthorityFeeDto.parse({
      ...rest,
      idempotencyKey,
      drivingLicenseClass,
      isDrivingLicenseClassAdvancement,
      validFrom: validFrom?.toISODate(),
      validUntil: validUntil?.toISODate(),
    });
    const serverClient = await getAuthenticatedServerClient();
    const dto = await serverClient.createExamAuthorityFee(createDto);
    return { data: _dtoToRecord(dto) };
  }

  async update(
    _resource: string,
    { id, data }: UpdateParams<ExamAuthorityFee>,
  ): Promise<UpdateResult<ExamAuthorityFee>> {
    const { validFrom, validUntil, drivingLicenseClassLabel, ...rest } = data;
    if (!drivingLicenseClassLabel) throw new Error("Property drivingLicenseClassLabel must be provided.");

    const [drivingLicenseClass, isDrivingLicenseClassAdvancement] =
      _splitDrivingLicenseClassLabel(drivingLicenseClassLabel);

    const updateDto = schemas.UpdateExamAuthorityFeeDto.parse({
      ...rest,
      validFrom: validFrom?.toISODate(),
      validUntil: validUntil?.toISODate(),
      drivingLicenseClass,
      isDrivingLicenseClassAdvancement,
    });

    const serverClient = await getAuthenticatedServerClient();
    const dto = await serverClient.updateExamAuthorityFee(updateDto, { params: { id } });
    return { data: _dtoToRecord(dto) };
  }
}

function _dtoToRecord(dto: ExamAuthorityFeeDto): ExamAuthorityFee {
  let drivingLicenseClassLabel: DrivingLicenseClassLabel;
  if (dto.drivingLicenseClass === "A" && dto.isDrivingLicenseClassAdvancement) {
    drivingLicenseClassLabel = "A Aufstieg";
  } else if (dto.drivingLicenseClass === "A2" && dto.isDrivingLicenseClassAdvancement) {
    drivingLicenseClassLabel = "A2 Aufstieg";
  } else {
    drivingLicenseClassLabel = dto.drivingLicenseClass;
  }
  return {
    ...dto,
    validFrom: DateTime.fromISO(dto.validFrom),
    validUntil: dto.validUntil ? DateTime.fromISO(dto.validUntil) : null,
    drivingLicenseClassLabel,
    theoryExamFee: dto.theoryExamFee ? MoneySchema.parse(dto.theoryExamFee) : null,
    practicalExamFee: dto.practicalExamFee ? MoneySchema.parse(dto.practicalExamFee) : null,
  };
}

function _splitDrivingLicenseClassLabel(
  drivingLicenseClassLabel: DrivingLicenseClassLabel,
): [DrivingLicenseClass, boolean] {
  if (drivingLicenseClassLabel === "A Aufstieg") {
    return ["A", true];
  } else if (drivingLicenseClassLabel === "A2 Aufstieg") {
    return ["A2", true];
  } else {
    return [drivingLicenseClassLabel, false];
  }
}

export const examAuthorityFeesProvider = new ExamAuthorityFeesProvider();
